import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import { Spring } from 'react-spring/renderprops';

import logo from 'assets/images/logo/logo_white.png';

import { APIHandler, Background, Nav, StorageHandler } from 'config/C4';
import { storageKeys } from 'config/settings';

const domains = {
  stewardship: {
    appId: 855,
    appName: 'Stewardship',
  },
  metrics: {
    appId: 856,
    appName: 'Metrics',
  },
  localSupply: {
    appId: 857,
    appName: 'Supply Chains',
  },
  climate: {
    appId: 858,
    appName: 'Climate',
  },
};

const calculateProgress = (obj) =>
  Math.floor(
    obj.reduce((total, { progress, total_pages: totalPages }) => total + Math.floor(+progress / +totalPages) * 100, 0) /
      obj.length,
  );

const setAppId = async (appId) => {
  const localId = await StorageHandler.load(storageKeys.appId);
  const res = await StorageHandler.load(storageKeys.user);
  if (res) {
    const { email } = res.response;

    if (localId !== appId) {
      StorageHandler.removeKeys(
        Object.keys(storageKeys).filter(
          (key) => ![storageKeys.appId, storageKeys.lang, storageKeys.user].includes(storageKeys[key]),
        ),
      );
    }

    const userInfo = await APIHandler.getUser(appId, email);
    if (APIHandler.ok(userInfo.response)) {
      const { year } = StorageHandler.expire;
      StorageHandler.save(storageKeys.user, userInfo, year);
    }
  }
  return StorageHandler.save(storageKeys.appId, appId, 86400000 * 365);
};

const getInfo = async (callback) => {
  const { response: user } = await StorageHandler.load(storageKeys.user);

  callback(
    Object.keys(domains).map((key) => {
      const { appId, appName } = domains[key];
      return { name: key, appId, appName };
    }),
  );

  const info = await Promise.all(
    Object.keys(domains).map(async (key) => {
      const { appId, appName } = domains[key];
      const { data } = (await APIHandler.getUserProgress(appId, user.email)) || { data: [{}] };
      const [{ chapters }] = data;

      const progress = calculateProgress(chapters);

      return { name: key, appId, appName, progress };
    }),
  );

  callback(info);
};

const getProgress = async (callback) => {
  const appProgress = {};
  await Promise.all(
    Object.keys(domains).map(async (key) => {
      const { appId } = domains[key];
      const { response: user } = await StorageHandler.load(storageKeys.user);

      const { data } = (await APIHandler.getUserProgress(appId, user.email)) || { data: [{}] };
      const [{ chapters }] = data;

      appProgress[key] =
        chapters.reduce(
          (total, { total_pages: totalPages, progress }) => total + Math.floor((progress / totalPages) * 100),
          0,
        ) / chapters.length;
    }),
  );
  callback(appProgress);
};

const AppSelector = ({ className = null, onSelect = () => undefined }) => {
  const [info, setInfo] = useState([]);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    if (Object.keys(progress).length === 0) {
      getProgress(setProgress);
      getInfo(setInfo);
    }
  }, [setProgress, progress]);

  return (
    <div className={clsx('wrapper__selector', { [className]: className })}>
      <div className="bottomBar" />
      <Background type="image" background="https://front.travpromobile.com/855/bgSize.jpg" />
      <a href="https://community.futureoftourism.org/" className="btn communityBtn" target="_blank" rel="noreferrer">
        {'Online Community'}
      </a>
      <Nav />
      <div className="logosLeft">
        <img
          src="https://front.travpromobile.com/855/large-centerforresponsibletravel.png"
          className="logo1"
          alt="center for responsible travel"
        />
        <img
          src="https://front.travpromobile.com/855/large-destinationstewardshipcenter.png"
          className="logo2"
          alt="desintations stewardship center"
        />
        <img
          src="https://front.travpromobile.com/855/large-greendestinations.png"
          className="logo3"
          alt="green destinations"
        />
      </div>

      <div className="logosRight">
        <img
          src="https://front.travpromobile.com/855/large-sustainabletravelinternational.png"
          className="logo1"
          alt="sustainable international"
        />
        <img
          src="https://front.travpromobile.com/855/large-thetravelfoundation.png"
          className="logo2"
          alt="the travel foundation"
        />
        <img src="https://front.travpromobile.com/855/large-tourismcares.png" className="logo3" alt="tourism cares" />
      </div>
      <Spring from={{ transform: 'translateY(30%)', opacity: 0 }} to={{ transform: 'translateY(0%)', opacity: 1 }}>
        {(props) => (
          <div className="wrapper__selector--title" style={props}>
            <div className="title__image">
              <img src={logo} alt="Future of Tourism Toolkit" />
            </div>
            <div className="title__divider" />
            <div className="title__text">
              <h1>{'Your online toolkit to build a better Future for Tourism'}</h1>
              <a href="http://www.futureoftourism.org/" target="_blank" type="button" className="btn" rel="noreferrer">
                {'About Us'}
              </a>
            </div>
          </div>
        )}
      </Spring>
      <div className="buttonBar">
        {info.map((item, i) => {
          const { appName, appId } = item;
          const key = i;
          return (
            <button
              key={key}
              type="button"
              onClick={async () => {
                await setAppId(appId);
                onSelect(appId);
              }}
            >
              <span>{appName}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AppSelector;

AppSelector.propTypes = {
  className: PropTypes.string,
  onSelect: PropTypes.func,
};
